<template>
  <section class="block rounded-lg mb-8 shadow bg-white w-full p-4 outline-none">
    <h1 class="text-3xl font-normal mb-4">Courses</h1>
    <h2 class="text-xl font-normal mt-8">
      Search Courses
    </h2>
    <BaseAccordion>
      <template #trigger="{ setIsOpen, isOpen }">
        <div class="search-wrapper flex mt-4">
          <SearchInput
            v-model="searchQuery"
            placeholder="Search for courses..."
            :is-loading="isLoading"
          />
          <BaseButton
            v-if="!hasDepartmentFilter"
            aria-label="Advanced filters"
            aria-expanded="true"
            :type="isOpen ? 'primary' : 'secondary'"
            class="ml-4 bg-color color-theme-base hover:opacity-80 "
            @click="setIsOpen(!isOpen)"
          >
            <span class="visually-hidden">Toggle advanced filters</span>
            <BaseIcon icon="filter" />
          </BaseButton>
          <span
            class="visually-hidden"
            role="status"
          >
            <template v-if="isOpen">Advanced filters open</template>
            <template v-else>Advanced filters collapsed</template>
          </span>
        </div>
      </template>
      <template #content>
        <div class="bg-white mb-4 p-4 -mt-8">
          <h4 class="font-semibold pt-2">Advanced Search</h4>
          <DepartmentFilter v-model="departmentFilter" />
        </div>
      </template>
    </BaseAccordion>
    <h2 class="text-lg font-normal flex justify-between">
      Results
      <BasePagination
        v-if="total > pageSize"
        :current-page="currentPage"
        :total-pages="totalPages"
        @page-change="updatePage"
      />
    </h2>
    <p role="status">
      <span v-if="!searchQuery.length && !departmentFilter">
        No search in progress.
      </span>
      <span v-else-if="isLoading">Search in progress</span>
      <span v-else-if="!courses.length">No results</span>
      <span
        v-else
        class="visually-hidden"
      >{{ courses.length }} results found</span>
    </p>
    <div v-if="total">
      <CoursesTable
        style="margin-bottom: 0 !important"
        :courses="coursesWithDepartment"
      />
    </div>

    <template v-if="chosenDepartments.length">
      <h2 class="text-xl font-normal mt-8 ">
        Browse Courses
      </h2>
      <div class="flex flex-wrap mt-4 -mx-2">
        <NuxtLink
          v-for="(department, index) of chosenDepartments"
          :key="index"
          class="md:w-1/3 px-2"
          :to="`/departments/${encodeURIComponent(department.id)}/courses`"
        >
          <div class="card p-4 md:mb-4 font-semibold">
            {{ department.displayName }}
          </div>
        </NuxtLink>
      </div>
    </template>
  </section>
</template>

<script>
import { mapState } from 'vuex'
import { getShortDescription } from '@/utils/generalUtils'
import SearchInput from '@/components/SearchInput'
import DepartmentFilter from '@/components/DepartmentFilter'
import pageLayout from '@/mixins/pageLayoutMixin'
import useCourses from '@/composables/useCourses'
import CoursesTable from '@/themes/cuny/CoursesTable'
import useMeta from '@/composables/useMeta'

export default {
  components: { SearchInput, DepartmentFilter, CoursesTable },
  mixins: [pageLayout],
  setup(props, { root }) {
    const columns = [
      'displayName',
      'department',
      'name',
      'courseNumber',
      'subjectCode',
      'code',
      'courseGroupId',
      'credits.creditHours',
      'longName',
      'career',
      'components',
      'customFields.catalogRequirementDesignation',
      'customFields.catalogAttributes',
    ]

    const {
      updatePage,
      search,
      fetchData,
      coursesWithDepartment,
      hasDepartmentFilter,
      totalPages,
      departmentFilter,
      courses,
      filters,
      total,
      currentPage,
      isLoading,
      searchQuery,
      pageSize,
    } = useCourses(root, { pageSize: 20, columns })

    const { catalogWideMetaTitle } = useMeta()

    return {
      updatePage,
      search,
      fetchData,
      coursesWithDepartment,
      hasDepartmentFilter,
      totalPages,
      departmentFilter,
      courses,
      filters,
      total,
      currentPage,
      isLoading,
      searchQuery,
      pageSize,
      catalogWideMetaTitle,
    }
  },
  head() {
    return {
      title: `Courses | ${this.catalogWideMetaTitle}`,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: `Courses | ${this.catalogWideMetaTitle}`,
        },
      ],
    }
  },
  computed: {
    ...mapState('departments', {
      chosenDepartments: (state) => state.chosen || state.all,
    }),
  },
  watch: {
    'filters.department': 'search',
    '$route.query': 'fetchData',
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    getShortDescription,
  },
}
</script>

<style lang="css" scoped>
.search-wrapper /deep/ .card {
  display: flex !important;
  margin-bottom: 0;
}

.bg-color {
  background-color: white !important;
}

@screen md {
  .courses-list {
    column-count: 3;
  }
}
</style>
